/**
 * cart-gifts.js — local replacement for the the previous vendor-supplied
 * <cart-drawer-gift> element (and an inert <gift-progress> placeholder).
 *
 * Rendered by:  snippets/cart-gift.liquid:57-226
 * Render sites: snippets/cart-drawer.liquid:512 (position: body, inside
 *               .drawer__inner but OUTSIDE #CartDrawer-CartItems)
 *               snippets/cart-drawer.liquid:805 (position: footer, inside
 *               .drawer__footer)
 *
 * WHAT THE ELEMENT DOES
 * The server decides once per render whether the gift is unlocked
 * (cart-gift.liquid:43 `if goal_max_reference >= requirement`) and bakes that
 * into data-selected, the progress <h3> and the lock overlay. This element's
 * only job is to make the CART agree with that decision after client-side cart
 * mutations: add the gift variant when the threshold is met, remove it when it
 * is not, then re-render the cart-drawer section so Liquid recomputes the copy.
 *
 * The progress text CANNOT be recomputed here: neither the raw copy templates
 * (block.settings.locked_progress_text / unlocked_progress_text) nor
 * shop.money_format are exposed on the element as data attributes — contrast
 * <gift-promo>, which does expose data-locked-text/data-unlocked-text/
 * data-money-format at snippets/gift-promo.liquid:140-144. A section re-render
 * is therefore the only way to refresh it.
 *
 * No Dawn upstream exists for this element. The cart write + Section Rendering
 * pattern follows the theme's own precedent in snippets/free-gifts.liquid
 * (postCart at :818-830, renderDrawer at :631-661).
 *
 * Vanilla ES2019. No imports, no build step, no external network calls.
 */
(function () {
  'use strict';

  /* This file is a classic script; a duplicated <script> tag must be a no-op. */
  if (window.__themeCartGiftsLoaded) return;
  window.__themeCartGiftsLoaded = true;

  /* ------------------------------------------------------------------ */
  /* Constants                                                           */
  /* ------------------------------------------------------------------ */

  /* sections/cart-drawer.liquid is the section that renders the gift. */
  var SECTION_ID = 'cart-drawer';

  /* snippets/cart-drawer.liquid:77-82 subtracts `cart-hidden` tagged items
     from cart.item_count to build `non_upsell_count`, which is what every
     render site passes as `items_count`. */
  var HIDDEN_TAG = 'cart-hidden';

  /* One coalesced pass per burst of cart activity. free-gifts.liquid uses
     150ms after an intercepted write and 400ms after `cart:updated`
     (snippets/free-gifts.liquid:923, :951); 250ms sits between the two so we
     settle after its fast path but before its slow one. */
  var RECONCILE_DELAY = 250;

  /* On boot, let the free-gifts engine finish its own reconciliation first
     (it runs sync() at snippets/free-gifts.liquid:955-957). */
  var BOOT_DELAY = 600;

  /* Runaway guard: if the cart and the thresholds ever disagree in a way that
     makes us write forever, stop acting rather than hammer the store. */
  var MAX_MUTATIONS = 6;
  var MUTATION_WINDOW = 10000;

  /* data-save-local-storage="true" + data-main-product (cart-gift.liquid:64-67)
     have no defined key or shape anywhere in the theme. We record the
     association so a future cross-cluster decision has the data, but nothing
     in this file reads it back — see the followUps in the handover notes. */
  var STORAGE_KEY = 'theme:cart-drawer-gift:v1';
  var STORAGE_TTL = 30 * 24 * 60 * 60 * 1000;

  function noop() {}

  /* ------------------------------------------------------------------ */
  /* Routes / network                                                    */
  /* ------------------------------------------------------------------ */

  /* window.routes is defined at layout/theme.liquid:672-679 and is path-only
     (no .js suffix). Fall back to the canonical paths if it is missing. */
  function giftRoute(key, fallback) {
    var value = window.routes && window.routes[key];
    return typeof value === 'string' && value ? value : fallback;
  }

  function giftAddUrl() {
    return giftRoute('cart_add_url', '/cart/add') + '.js';
  }

  function giftChangeUrl() {
    return giftRoute('cart_change_url', '/cart/change') + '.js';
  }

  function giftCartUrl() {
    return giftRoute('cart_url', '/cart') + '.js';
  }

  function giftSectionsUrl() {
    return window.location.pathname + window.location.search;
  }

  /* Deliberately uses window.fetch (not a captured native reference) so that
     the free-gifts interceptor at snippets/free-gifts.liquid:915-928 still
     sees our cart writes and keeps its own state in sync. */
  function giftPost(url, payload) {
    return window
      .fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload)
      })
      .then(function (res) {
        return res
          .json()
          .catch(function () {
            return null;
          })
          .then(function (data) {
            return { ok: res.ok, data: data };
          });
      })
      .catch(function () {
        return { ok: false, data: null };
      });
  }

  function giftGetCart() {
    return window
      .fetch(giftCartUrl(), { cache: 'no-store', headers: { Accept: 'application/json' } })
      .then(function (res) {
        return res.json();
      })
      .catch(function () {
        return null;
      });
  }

  /* ------------------------------------------------------------------ */
  /* Cart inspection                                                     */
  /* ------------------------------------------------------------------ */

  /* /cart.js line items expose `handle` (the product handle). `url` is used
     only as a defensive fallback. The gift line is located by handle because
     that is the locator the Liquid itself uses: the block's inline <style> at
     cart-gift.liquid:53 targets .cart-item--product-{{ product.handle }},
     emitted at snippets/cart-drawer.liquid:143. */
  function giftLineHandle(item) {
    if (item && item.handle) return String(item.handle);
    var match = /\/products\/([^/?#]+)/.exec((item && item.url) || '');
    return match ? match[1] : '';
  }

  function giftFindLine(cart, handle) {
    if (!cart || !Array.isArray(cart.items) || !handle) return null;
    for (var i = 0; i < cart.items.length; i++) {
      if (giftLineHandle(cart.items[i]) === handle) return cart.items[i];
    }
    return null;
  }

  /* Mirrors `non_upsell_count` (snippets/cart-drawer.liquid:77-82). This is
     the exact idiom the theme already uses for the same rule at
     snippets/free-gifts.liquid:616. */
  function giftVisibleQuantity(cart) {
    var count = typeof cart.item_count === 'number' ? cart.item_count : 0;
    (cart.items || []).forEach(function (item) {
      if (item.product_tags && item.product_tags.indexOf(HIDDEN_TAG) !== -1) {
        count -= item.quantity;
      }
    });
    return count;
  }

  /* cart-gift.liquid:39 measures against cart.items_subtotal_price — NOT
     cart.total_price. Do not substitute the `data-subtotal` attribute on
     <cart-drawer-items> (snippets/cart-drawer.liquid:126); that one is
     cart.total_price and differs once a discount applies. */
  function giftSubtotal(cart) {
    if (typeof cart.items_subtotal_price === 'number') return cart.items_subtotal_price;
    var sum = 0;
    (cart.items || []).forEach(function (item) {
      sum += item.final_line_price || 0;
    });
    return sum;
  }

  /* ------------------------------------------------------------------ */
  /* Element helpers                                                     */
  /* ------------------------------------------------------------------ */

  function giftAll() {
    return Array.prototype.slice.call(document.querySelectorAll('cart-drawer-gift'));
  }

  function giftAttr(el, name) {
    var value = el.getAttribute(name);
    return value == null ? '' : String(value).trim();
  }

  /* The hidden input is the live source of truth once the variant picker has
     written to it (cart-gift.liquid:211); data-id (cart-gift.liquid:61) is the
     server-rendered default. */
  function giftVariantId(el) {
    var input = el.querySelector('form[data-type="add-to-cart-form"] input[name="id"]') || el.querySelector('input[name="id"]');
    var id = input && input.value ? String(input.value).trim() : '';
    return id || giftAttr(el, 'data-id');
  }

  function giftSelects(el) {
    var picker = el.querySelector('.upsell__variant-picker');
    if (!picker) return [];
    return Array.prototype.slice.call(picker.querySelectorAll('select.variant-dropdown'));
  }

  /* ------------------------------------------------------------------ */
  /* Threshold evaluation                                                */
  /* ------------------------------------------------------------------ */

  /* data-requirement (cart-gift.liquid:63) is `block.settings.requirement |
     times: goal_multiplier`, and both multipliers are FLOAT literals
     (cart-gift.liquid:35, :38) — so Liquid emits "8000.0" / "80.0", never
     "8000". parseInt would silently truncate at the decimal point on some
     values; parseFloat is required. */
  function giftRequirement(el) {
    return parseFloat(giftAttr(el, 'data-requirement'));
  }

  /* Exactly two values are possible (cart-gift.liquid:62). */
  function giftMetric(el, cart) {
    return giftAttr(el, 'data-type') === 'product_quantity' ? giftVisibleQuantity(cart) : giftSubtotal(cart);
  }

  /* ------------------------------------------------------------------ */
  /* Section rendering                                                   */
  /* ------------------------------------------------------------------ */

  /* The selects at cart-gift.liquid:166-174 never carry a `selected`
     attribute, so a re-render always snaps the picker back to its first
     option. Snapshot the customer's choice and restore it afterwards. */
  function giftSnapshotPickers() {
    var snapshot = [];
    giftAll().forEach(function (el) {
      try {
        var selects = giftSelects(el);
        if (!selects.length) return;
        snapshot.push({
          handle: giftAttr(el, 'data-handle'),
          values: selects.map(function (select) {
            return select.value;
          }),
          variantId: giftVariantId(el)
        });
      } catch (e) {
        /* one broken block must never abort the render */
      }
    });
    return snapshot;
  }

  function giftRestorePickers(snapshot) {
    if (!snapshot || !snapshot.length) return;
    var used = {};
    giftAll().forEach(function (el) {
      try {
        var selects = giftSelects(el);
        /* Pair against the snapshot on the same condition that built it,
           otherwise the occurrence counters drift apart. */
        if (!selects.length) return;

        var handle = giftAttr(el, 'data-handle');
        var seen = (used[handle] = (used[handle] || 0) + 1);
        var hits = 0;
        var entry = null;
        for (var i = 0; i < snapshot.length; i++) {
          if (snapshot[i].handle !== handle) continue;
          if (++hits === seen) {
            entry = snapshot[i];
            break;
          }
        }
        if (!entry) return;

        selects.forEach(function (select, index) {
          var wanted = entry.values[index];
          if (wanted == null) return;
          /* Only restore a value the fresh markup actually offers. */
          for (var j = 0; j < select.options.length; j++) {
            if (select.options[j].value === wanted) {
              select.value = wanted;
              return;
            }
          }
        });

        if (entry.variantId) {
          var input = el.querySelector('input[name="id"]');
          if (input) input.value = entry.variantId;
          el.setAttribute('data-id', entry.variantId);
        }
      } catch (e) {
        /* ignore */
      }
    });
  }

  function giftSwapInner(doc, selector) {
    var fresh = doc.querySelector(selector);
    var live = document.querySelector(selector);
    if (fresh && live) live.innerHTML = fresh.innerHTML;
  }

  /* Closes the gap documented in the DOM contract: a `position: body` gift
     lives in .drawer__inner (snippets/cart-drawer.liquid:33) but OUTSIDE
     #CartDrawer-CartItems (:136), so none of the three region swaps below
     reach it. free-gifts.liquid:641-658 has the same blind spot.
     Replaces the whole div.cart-drawer-gift-container (cart-gift.liquid:2) so
     the inline CSS custom properties at cart-gift.liquid:3-18 refresh too. */
  function giftSwapContainers(doc) {
    var fresh = Array.prototype.slice.call(doc.querySelectorAll('cart-drawer-gift'));
    if (!fresh.length) return;

    /* Index the live gifts by handle. The schema requires unique gift products
       (sections/cart-drawer.liquid:2102), but pair by occurrence anyway so a
       misconfigured store stays deterministic. */
    var byHandle = {};
    giftAll().forEach(function (el) {
      var handle = giftAttr(el, 'data-handle');
      (byHandle[handle] = byHandle[handle] || []).push(el);
    });

    var seen = {};
    var pending = [];
    fresh.forEach(function (freshEl) {
      var handle = giftAttr(freshEl, 'data-handle');
      var index = (seen[handle] = (seen[handle] || 0) + 1) - 1;
      var liveEl = byHandle[handle] && byHandle[handle][index];
      if (!liveEl) return;

      var freshBox = freshEl.closest('.cart-drawer-gift-container') || freshEl;
      var liveBox = liveEl.closest('.cart-drawer-gift-container') || liveEl;
      if (!liveBox.parentNode) return;
      /* Gifts inside a region we already swapped are identical — skip them
         rather than churn the DOM a second time. */
      if (liveBox.outerHTML === freshBox.outerHTML) return;
      pending.push([liveBox, freshBox.outerHTML]);
    });

    pending.forEach(function (pair) {
      try {
        /* outerHTML assignment upgrades the new custom elements synchronously. */
        pair[0].outerHTML = pair[1];
      } catch (e) {
        /* ignore */
      }
    });
  }

  /* Mirrors snippets/free-gifts.liquid:631-661: swapping only
     #CartDrawer-CartItems cannot render the empty state, because that element
     is absent from the empty markup. */
  function giftRender(html) {
    if (!html) return;
    var snapshot = giftSnapshotPickers();
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var fresh = doc.querySelector('cart-drawer');
    var live = document.querySelector('cart-drawer');
    var flipped = false;

    if (fresh && live) {
      var freshEmpty = fresh.classList.contains('is-empty');
      flipped = freshEmpty !== live.classList.contains('is-empty');
      if (flipped) {
        var freshInner = fresh.querySelector('.drawer__inner');
        var liveInner = live.querySelector('.drawer__inner');
        if (freshInner && liveInner) liveInner.innerHTML = freshInner.innerHTML;
        /* Toggled after the swap so .is-empty styling hits the new markup.
           Other classes are left alone to preserve the open/close state. */
        live.classList.toggle('is-empty', freshEmpty);
      }
    }

    if (!flipped) {
      ['#CartDrawer-CartItems', '.drawer__footer', '.drawer__header'].forEach(function (selector) {
        giftSwapInner(doc, selector);
      });
      giftSwapContainers(doc);
    }

    giftRestorePickers(snapshot);

    /* The theme's only cart-mutation broadcast (dispatched at
       snippets/free-gifts.liquid:660, subscribed at :948). There is no
       publish/subscribe bus in this theme — do not invent one. */
    document.dispatchEvent(new CustomEvent('cart:updated'));
  }

  function giftFetchSection() {
    var url = giftSectionsUrl();
    url += (url.indexOf('?') === -1 ? '?' : '&') + 'section_id=' + SECTION_ID;
    return window
      .fetch(url)
      .then(function (res) {
        return res.text();
      })
      .then(giftRender)
      .catch(noop);
  }

  function giftRenderFrom(html) {
    if (html) {
      giftRender(html);
      return Promise.resolve();
    }
    /* The write did not carry the `sections` payload (or it failed) — go and
       fetch the section instead. */
    return giftFetchSection();
  }

  /* ------------------------------------------------------------------ */
  /* Reconciliation                                                      */
  /* ------------------------------------------------------------------ */

  /* Every cart write from every gift block funnels through this one chain so
     that concurrent blocks cannot clobber each other's section re-render. */
  var giftQueue = Promise.resolve();
  var giftTimer = null;
  var giftBooted = false;
  var giftMutations = [];

  function giftEnqueue(job) {
    giftQueue = giftQueue.then(job).catch(noop);
    return giftQueue;
  }

  function giftSchedule(delay) {
    clearTimeout(giftTimer);
    giftTimer = setTimeout(function () {
      giftEnqueue(giftReconcile);
    }, typeof delay === 'number' ? delay : RECONCILE_DELAY);
  }

  function giftBudgetAvailable() {
    var now = Date.now();
    giftMutations = giftMutations.filter(function (t) {
      return now - t < MUTATION_WINDOW;
    });
    return giftMutations.length < MAX_MUTATIONS;
  }

  /* Bridges the gap between the cart write landing and the section re-render
     arriving. CSS reads data-selected (assets/base.css: .upsell--border
     [data-selected="true"], .upsell--custom-bg[data-selected="true"]), so a
     stale value is visibly wrong for those few hundred ms. The re-render
     replaces the node outright, restoring the server's own value. */
  function giftMarkOptimistic(step) {
    try {
      var el = step.el;
      if (!el || !el.parentNode) return;
      var unlocked = step.action === 'add';
      el.setAttribute('data-selected', unlocked ? 'true' : 'false');
      var lock = el.querySelector('.cart-drawer-gift__image__locked');
      /* .cart-drawer-gift__image__locked is display:flex in assets/base.css,
         so only the theme's .hidden (display:none !important) can hide it. */
      if (lock) lock.classList.toggle('hidden', unlocked);
    } catch (e) {
      /* ignore */
    }
  }

  function giftPlan(elements, cart) {
    var steps = [];
    var claimed = {};

    elements.forEach(function (el) {
      try {
        var handle = giftAttr(el, 'data-handle');
        /* One action per gift product per pass. */
        if (!handle || claimed[handle]) return;

        var requirement = giftRequirement(el);
        /* Blank / non-numeric requirement: the intent is undefined, so leave
           the cart completely alone rather than guess. A requirement of 0 is
           finite and unlocks unconditionally, exactly as the schema help text
           at sections/cart-drawer.liquid:2245 promises. */
        if (!isFinite(requirement)) return;

        /* cart-gift.liquid:43 — the comparison is inclusive. */
        var unlocked = giftMetric(el, cart) >= requirement;
        var line = giftFindLine(cart, handle);

        if (unlocked && !line) {
          var variantId = giftVariantId(el);
          if (!variantId) return;
          claimed[handle] = true;
          steps.push({ el: el, action: 'add', handle: handle, id: variantId });
        } else if (!unlocked && line) {
          if (!line.key) return;
          claimed[handle] = true;
          steps.push({ el: el, action: 'remove', handle: handle, key: line.key });
        }
      } catch (e) {
        /* a single malformed block must not stop the others */
      }
    });

    return steps;
  }

  function giftApply(steps) {
    var lastHtml = null;

    return steps
      .reduce(function (chain, step, index) {
        return chain.then(function () {
          var last = index === steps.length - 1;
          var url = step.action === 'add' ? giftAddUrl() : giftChangeUrl();
          /* The gift form has no quantity input (cart-gift.liquid:204-223), so
             Shopify's own default of 1 is the target quantity. */
          var payload = step.action === 'add' ? { id: step.id, quantity: 1 } : { id: step.key, quantity: 0 };

          /* Ask for the re-rendered drawer alongside the final write so the
             markup and the cart cannot disagree. Precedent: postCart at
             snippets/free-gifts.liquid:818-830. */
          if (last) {
            payload.sections = SECTION_ID;
            payload.sections_url = giftSectionsUrl();
          }

          return giftPost(url, payload).then(function (res) {
            if (!res.ok) return;
            giftMutations.push(Date.now());
            giftMarkOptimistic(step);
            if (last && res.data && res.data.sections) {
              lastHtml = res.data.sections[SECTION_ID];
            }
          });
        });
      }, Promise.resolve())
      .then(function () {
        return lastHtml;
      });
  }

  function giftReconcile() {
    giftBooted = true;
    var elements = giftAll();
    if (!elements.length) return Promise.resolve();

    return giftGetCart().then(function (cart) {
      if (!cart || !Array.isArray(cart.items)) return;

      var steps = giftPlan(elements, cart);
      if (!steps.length) return;

      if (!giftBudgetAvailable()) {
        /* Something is oscillating. Stop writing rather than loop forever. */
        if (window.console && console.warn) {
          console.warn('[cart-drawer-gift] too many cart writes in a short window — pausing auto add/remove.');
        }
        return;
      }

      return giftApply(steps).then(giftRenderFrom);
    });
  }

  /* ------------------------------------------------------------------ */
  /* Persistence (record only — nothing here reads it back)              */
  /* ------------------------------------------------------------------ */

  function giftRemember(el) {
    if (giftAttr(el, 'data-save-local-storage') !== 'true') return;
    var mainProduct = giftAttr(el, 'data-main-product');
    var handle = giftAttr(el, 'data-handle');
    if (!mainProduct || !handle) return;

    try {
      var now = Date.now();
      var store = {};
      var raw = window.localStorage.getItem(STORAGE_KEY);
      if (raw) {
        var parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') store = parsed;
      }
      /* Bound the record so it cannot grow without limit. */
      Object.keys(store).forEach(function (key) {
        var entry = store[key];
        if (!entry || typeof entry.savedAt !== 'number' || now - entry.savedAt > STORAGE_TTL) {
          delete store[key];
        }
      });
      store[handle] = { mainProduct: mainProduct, variantId: giftAttr(el, 'data-id'), savedAt: now };
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
    } catch (e) {
      /* Safari private mode and quota errors must never break add-to-cart. */
    }
  }

  /* ------------------------------------------------------------------ */
  /* <cart-drawer-gift>                                                  */
  /* ------------------------------------------------------------------ */

  class CartDrawerGift extends HTMLElement {
    connectedCallback() {
      try {
        this.giftBindPicker();
        giftRemember(this);
      } catch (e) {
        /* never let setup failure block the reconcile below */
      }
      /* Debounced globally, so a drawer re-render carrying three gift blocks
         still costs exactly one /cart.js read. giftBooted is flipped by the
         reconcile itself, not here — otherwise the second block to connect in
         the same parse tick would shorten the first block's boot window. */
      giftSchedule(giftBooted ? RECONCILE_DELAY : BOOT_DELAY);
    }

    /* --- variant picker (cart-gift.liquid:133-184) ------------------- */

    giftBindPicker() {
      var selects = giftSelects(this);
      if (!selects.length) return;
      var self = this;
      selects.forEach(function (select) {
        select.addEventListener('change', function () {
          try {
            self.giftOnVariantChange();
          } catch (e) {
            /* a picker failure must not break the add path */
          }
        });
      });
    }

    /* {{ product.variants | json }} — cart-gift.liquid:181-183. Same
       convention as Dawn's variant-selects getVariantData(). */
    giftVariantData() {
      var picker = this.querySelector('.upsell__variant-picker');
      var script = picker && picker.querySelector('script[type="application/json"]');
      if (!script) return null;
      try {
        var data = JSON.parse(script.textContent);
        return Array.isArray(data) ? data : null;
      } catch (e) {
        return null;
      }
    }

    giftOnVariantChange() {
      var variants = this.giftVariantData();
      if (!variants) return;

      /* Selects render in product.options_with_values order (cart-gift.liquid:136),
         i.e. option1, option2, option3. Option TEXT may be the localised
         "unavailable" string (cart-gift.liquid:170) but the value attribute is
         always the raw option value, so matching on .value is safe. */
      var values = giftSelects(this).map(function (select) {
        return select.value;
      });
      if (!values.length) return;

      var match = null;
      for (var i = 0; i < variants.length; i++) {
        var variant = variants[i];
        var hit = true;
        for (var j = 0; j < values.length; j++) {
          if (String(variant['option' + (j + 1)]) !== String(values[j])) {
            hit = false;
            break;
          }
        }
        if (hit) {
          match = variant;
          break;
        }
      }
      if (!match) return;

      var input = this.querySelector('input[name="id"]');
      var previous = input ? String(input.value) : giftAttr(this, 'data-id');
      var next = String(match.id);

      if (input) input.value = next;
      /* Keep the element's own attribute coherent with the form. No CSS reads
         data-id, so this is presentation-neutral. Prices are deliberately NOT
         updated: the block exposes no data-money-format (contrast
         snippets/upsell-block.liquid:169-175), so they cannot be formatted. */
      this.setAttribute('data-id', next);

      if (previous && previous !== next) this.giftSwapCartLine(next);
    }

    /* If the gift is already unlocked and sitting in the cart, move the line to
       the newly chosen variant. Otherwise the customer picks "Large" and
       silently keeps "Small". */
    giftSwapCartLine(variantId) {
      var handle = giftAttr(this, 'data-handle');
      if (!handle) return;

      giftEnqueue(function () {
        if (!giftBudgetAvailable()) return;

        return giftGetCart().then(function (cart) {
          var line = giftFindLine(cart, handle);
          if (!line || !line.key) return;
          if (String(line.variant_id) === String(variantId)) return;

          var quantity = line.quantity || 1;
          return giftPost(giftChangeUrl(), { id: line.key, quantity: 0 })
            .then(function (res) {
              if (!res.ok) return null;
              giftMutations.push(Date.now());
              return giftPost(giftAddUrl(), {
                id: variantId,
                quantity: quantity,
                sections: SECTION_ID,
                sections_url: giftSectionsUrl()
              });
            })
            .then(function (res) {
              if (!res) return;
              if (res.ok) giftMutations.push(Date.now());
              var html = res.data && res.data.sections && res.data.sections[SECTION_ID];
              return giftRenderFrom(html);
            });
        });
      });
    }
  }

  if (!customElements.get('cart-drawer-gift')) {
    customElements.define('cart-drawer-gift', CartDrawerGift);
  }

  /* ------------------------------------------------------------------ */
  /* <gift-progress>                                                     */
  /* ------------------------------------------------------------------ */

  /**
   * The migration checklist lists `gift-progress`, but the tag is NEVER
   * rendered by this theme. Its only two occurrences are inside JavaScript
   * block comments in snippets/free-gifts.liquid (:573 and :960) that describe
   * its renamed successor, <gift-promo>.
   *
   * <gift-promo> is already implemented locally at
   * snippets/free-gifts.liquid:983-1045 behind its own
   * customElements.get('gift-promo') guard, and is live in production. It must
   * NOT be redefined, moved, or duplicated here.
   *
   * Registered as an inert placeholder purely so the tag is defined: it
   * upgrades nothing (no node with this tag exists) and adds no behaviour.
   * Delete it once the checklist is corrected.
   */
  class GiftProgressPlaceholder extends HTMLElement {}

  if (!customElements.get('gift-progress')) {
    customElements.define('gift-progress', GiftProgressPlaceholder);
  }

  /* ------------------------------------------------------------------ */
  /* Triggers                                                            */
  /* ------------------------------------------------------------------ */

  /* The theme's only cart-mutation broadcast. free-gifts.liquid dispatches it
     at the end of every renderDrawer() (:660). Our own giftRender() dispatches
     it too, but only after a real mutation, so this cannot loop: the follow-up
     pass finds nothing to do and writes nothing. */
  document.addEventListener('cart:updated', function () {
    giftSchedule(RECONCILE_DELAY);
  });
})();
